import { useState } from 'react';

import { Button, MonoLabel, StatusPill } from '@/design-system';
import type { CostKey, PublicFinding, PublicLanguage } from '@/entities/publicReport';

import type { PublicCopy } from '../copy';
import { findingExplanation, formatAmount } from '../format';
import { PublicBlock } from './PublicBlock';
import { SectionTitle } from './SectionTitle';

export interface KeyFindingsProps {
    findings: PublicFinding[];
    /** Staat het bedrijf toe dat de koper bedragen ziet? */
    showCosts: boolean;
    language: PublicLanguage;
    copy: PublicCopy;
    headingLevel: 2 | 3;
}

/** De urgentiepil leent de ernstkleuren: direct = gebrek, op termijn = aandacht. */
const urgencyTone = {
    direct: 'gebrek',
    termijn: 'aandacht',
    onderhoud: 'goed',
} as const satisfies Record<CostKey, 'goed' | 'aandacht' | 'gebrek'>;

/**
 * Zoveel kaarten staan er meteen; de rest komt achter één knop. De API levert
 * ze op volgorde van urgentie en bedrag, dus bovenaan staat wat het zwaarst weegt.
 */
export const FINDINGS_SHOWN_FIRST = 5;

/**
 * De bevindingen in kopers-taal ("zacht over ongeveer 40 cm"), nooit in
 * normtaal. De mono-regel eronder noemt sectie en bedrag.
 *
 * Een kaart toont de foto alleen als die er is, en de eerste alinea van de
 * toelichting zonder de zin die al als titel bovenaan staat: de volledige tekst
 * staat in de PDF.
 */
export function KeyFindings({
    findings,
    showCosts,
    language,
    copy,
    headingLevel,
}: KeyFindingsProps) {
    const [showAll, setShowAll] = useState(false);

    if (findings.length === 0) {
        return null;
    }

    const hasMore = findings.length > FINDINGS_SHOWN_FIRST;
    const shown = showAll || !hasMore ? findings : findings.slice(0, FINDINGS_SHOWN_FIRST);

    return (
        <PublicBlock>
            <SectionTitle level={headingLevel}>{copy.findingsTitle}</SectionTitle>

            <ul className="m-0 flex list-none flex-col gap-[10px] p-0">
                {shown.map((finding) => {
                    const explanation = findingExplanation(finding.title, finding.explanation);

                    return (
                        <li
                            key={finding.id}
                            className="flex flex-wrap gap-[14px] rounded-[10px] border border-[var(--border-default)] p-[14px]"
                        >
                            {finding.photoUrl ? (
                                <img
                                    src={finding.photoUrl}
                                    alt={finding.photoCaption ?? ''}
                                    className="h-[78px] w-[104px] shrink-0 rounded-[7px] border border-[var(--placeholder-border)] object-cover"
                                />
                            ) : null}

                            <div className="min-w-0 flex-[1_1_240px]">
                                <div className="flex flex-wrap items-center gap-[9px]">
                                    <span className="text-[15px] leading-[1.3] font-medium text-[var(--text-primary)]">
                                        {finding.title}
                                    </span>
                                    <StatusPill severity={urgencyTone[finding.urgency]} size="md">
                                        {copy.urgencyLabels[finding.urgency]}
                                    </StatusPill>
                                </div>

                                {explanation ? (
                                    <p className="mt-[6px] mb-0 line-clamp-4 text-[13px] leading-[1.5] whitespace-pre-line text-pretty text-[var(--text-secondary)]">
                                        {explanation}
                                    </p>
                                ) : null}

                                {/* Wat, dan waar, dan waar het hoort en wat het kost - de volgorde waarin je een gebrek leest. */}
                                {finding.location ? (
                                    <p className="mt-[6px] mb-0 text-[13px] leading-[1.5] text-pretty text-[var(--text-secondary)]">
                                        <span className="font-medium text-[var(--text-primary)]">
                                            {copy.findingLocation}
                                        </span>{' '}
                                        {finding.location}
                                    </p>
                                ) : null}

                                <MonoLabel
                                    size={11}
                                    variant="value"
                                    tone="muted"
                                    className="mt-2 block break-words"
                                >
                                    {showCosts && finding.amount !== null
                                        ? `${finding.section} · ${formatAmount(finding.amount, language)}`
                                        : finding.section}
                                </MonoLabel>
                            </div>
                        </li>
                    );
                })}
            </ul>

            {hasMore ? (
                <Button
                    variant="outline"
                    size="md"
                    className="mt-3"
                    aria-expanded={showAll}
                    onClick={() => setShowAll((current) => !current)}
                >
                    {showAll ? copy.findingsShowFewer : copy.findingsShowAll(findings.length)}
                </Button>
            ) : null}
        </PublicBlock>
    );
}
